<script setup lang="ts">
  import Icon from '../Icon/Icon.vue'
  import type { TabItem } from '../Tab/Tab.vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const { settings } = useGlobalLayoutSettings()
  const title = settings.footerBrandName ?? settings.siteName
  const year = new Date().getFullYear()
  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)
  const showSettings = computed(
    () => featureFlags.value?.userSettings?.position !== 'off'
  )

  const selectedCategory = defineModel<string>('selected-category')
  const isMenuOpen = defineModel<boolean>('menu-open')
  const emit = defineEmits<{
    (event: 'tabCategoryClick', category: TabItem): void
  }>()

  function handleTabClick(tab: TabItem) {
    if (!tab.disableSelection) selectedCategory.value = tab.id
    if (tab.onClick) tab.onClick()
    emit('tabCategoryClick', tab)
  }
</script>
<template>
  <div
    class="lg:hidden w-screen absolute z-[100] bg-background-default overscroll-contain overflow-auto h-[calc(100vh_-_72px)]"
    :class="{
      'ml-0': isMenuOpen,
      'ml-[-100vw]': !isMenuOpen,
    }"
    style="transition: margin-left 0.5s cubic-bezier(0.82, 0.085, 0.395, 0.895)"
  >
    <slot name="menu">
      <div
        class="flex flex-col gap-3 p-3 border-t border-border-interaction-neutral-normal"
      >
        <!-- Menu category button  -->
        <button
          v-for="tab in settings.tabCategories"
          :key="tab.id"
          class="text-left py-[10px] px-[12px]"
          :class="{
            'border border-action-neutral-onsubtle-default rounded-[8px]':
              selectedCategory === tab.id,
          }"
          @click="handleTabClick(tab)"
        >
          <Typography
            weight="font-medium"
            variant="text-sm"
            color="text-action-neutral-default"
            class="hover:text-action-neutral-hover active:text-action-neutral-active animation-spin"
            :class="{
              'text-action-neutral-selected': selectedCategory === tab.id,
            }"
          >
            {{ t(tab.label) }}
          </Typography>
        </button>

        <slot name="menu-items" />
      </div>
      <!-- Footer -->
      <footer>
        <div
          class="flex flex-col gap-3 p-3 border-t border-border-interaction-neutral-normal"
        >
          <a v-if="title === 'OneNews'" href="/contact" target="_blank">
            <Typography
              weight="font-medium"
              variant="text-sm"
              color="text-action-neutral-default"
              class="text-left py-[10px] px-[12px]"
            >
              {{ t('Contact Us') }}
            </Typography>
          </a>
          <a href="/privacy-policy" target="_blank">
            <Typography
              weight="font-medium"
              variant="text-sm"
              color="text-action-neutral-default"
              class="text-left py-[10px] px-[12px]"
            >
              {{ t('Privacy Policy') }}
            </Typography>
          </a>
          <a href="/terms" target="_blank">
            <Typography
              weight="font-medium"
              variant="text-sm"
              color="text-action-neutral-default"
              class="text-left py-[10px] px-[12px]"
            >
              {{ t('Terms of Use') }}
            </Typography>
          </a>
        </div>
        <div
          class="flex flex-row pb-6 px-6 pt-3 border-t border-border-interaction-neutral-normal items-center justify-between"
        >
          <SettingsButton
            v-if="showSettings"
            class="text-left py-[10px] px-[12px] [&_*]:hover:no-underline"
          >
            <template #button>
              <Icon
                icon="settings"
                classes="h-6 w-6 dark:filter dark:brightness-[300] dark:invert-0"
              />
              <Typography
                weight="font-medium"
                variant="text-sm"
                color="text-action-neutral-default"
              >
                {{ t('Settings') }}
              </Typography>
            </template>
          </SettingsButton>
          <Typography
            element="span"
            weight="font-medium"
            variant="text-sm"
            color="text-action-neutral-default"
          >
            © {{ year }} {{ title }}
          </Typography>
        </div>
      </footer>
    </slot>
  </div>
</template>
